import { useState, useEffect } from 'react';

interface Props {
  onComplete: () => void;
}

export default function LoadingScreen({ onComplete }: Props) {
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState<'loading' | 'fadeout'>('loading');

  useEffect(() => {
    // Progress bar fill
    const startTime = Date.now();
    const duration = 2200;

    const raf = () => {
      const elapsed = Date.now() - startTime;
      const p = Math.min((elapsed / duration) * 100, 100);
      setProgress(p);
      if (p < 100) {
        requestAnimationFrame(raf);
      }
    };
    requestAnimationFrame(raf);

    // Start fadeout at 2.4s
    const fadeTimer = setTimeout(() => setPhase('fadeout'), 2400);
    // Call onComplete at 2.9s (after fade)
    const doneTimer = setTimeout(() => onComplete(), 2900);

    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(doneTimer);
    };
  }, [onComplete]);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: '#080809',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        transition: phase === 'fadeout' ? 'opacity 0.5s ease' : 'none',
        opacity: phase === 'fadeout' ? 0 : 1,
        pointerEvents: phase === 'fadeout' ? 'none' : 'all',
      }}
    >
      {/* Background grid */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'linear-gradient(rgba(220,38,38,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(220,38,38,0.04) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }}
      />

      {/* Radial glow behind logo */}
      <div
        style={{
          position: 'absolute',
          width: 400,
          height: 400,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(220,38,38,0.12) 0%, transparent 70%)',
          animation: 'loadGlow 1.8s ease-in-out infinite alternate',
        }}
      />

      {/* Logo image */}
      <div
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 16,
          animation: 'loadLogoIn 0.7s cubic-bezier(0.16,1,0.3,1) forwards',
          opacity: 0,
          animationDelay: '0.15s',
        }}
      >
        <img
          src="https://storage.readdy-site.link/project_files/e0ad0187-0f87-44ac-bc81-3163fcc0baf7/856a6a4e-c760-477d-a837-65755837c797_fb6affb0-c65b-4aac-81c4-bfd5377e0e55.webp?v=328770f95ae230a12f1326c5fd47e1ad"
          alt="SpartechRD"
          fetchpriority="high"
          loading="eager"
          style={{
            width: 72,
            height: 72,
            objectFit: 'contain',
            animation: 'loadIconPulse 1.4s ease-in-out 0.8s infinite alternate',
          }}
        />

        {/* Brand name */}
        <div style={{ textAlign: 'center' }}>
          <div className="loading-brand-text">
            SPAR<span style={{ color: '#dc2626' }}>TECH</span>
          </div>
          <div
            className="loading-sub-text"
            style={{
              fontWeight: 700,
              letterSpacing: '0.3em',
              color: '#52525b',
              textTransform: 'uppercase',
              marginTop: 8,
              animation: 'loadSubIn 0.5s ease forwards',
              opacity: 0,
              animationDelay: '0.65s',
            }}
          >
            HARDWARE · GAMING · PERFORMANCE
          </div>
        </div>

        {/* Animated separator */}
        <div
          style={{
            height: 1,
            background: 'linear-gradient(90deg, transparent, #dc2626, transparent)',
            animation: 'loadLineExpand 0.6s cubic-bezier(0.16,1,0.3,1) forwards',
            width: 0,
            animationDelay: '0.55s',
          }}
        />
      </div>

      {/* Bottom progress area */}
      <div
        style={{
          position: 'absolute',
          bottom: 36,
          left: '50%',
          transform: 'translateX(-50%)',
        }}
      >
        <div
          className="loading-progress-bar"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 10,
            animation: 'loadSubIn 0.5s ease forwards',
            opacity: 0,
            animationDelay: '0.8s',
          }}
        >
          {/* Progress track */}
          <div
            style={{
              width: '100%',
              height: 2,
              background: 'rgba(255,255,255,0.06)',
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                height: '100%',
                width: `${progress}%`,
                background: 'linear-gradient(90deg, #991b1b, #dc2626, #f87171)',
                borderRadius: 2,
                transition: 'width 0.1s linear',
                boxShadow: '0 0 8px rgba(220,38,38,0.6)',
              }}
            />
          </div>
          <span
            style={{
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: '0.35em',
              color: '#3f3f46',
              textTransform: 'uppercase',
            }}
          >
            CARGANDO SISTEMA
          </span>
        </div>
      </div>

      {/* CSS keyframes */}
      <style>{`
        .loading-brand-text {
          font-family: 'Rajdhani', 'Orbitron', sans-serif;
          font-size: 36px;
          font-weight: 900;
          letter-spacing: 0.18em;
          color: #ffffff;
          line-height: 1;
          text-transform: uppercase;
        }
        .loading-sub-text {
          font-size: 9px;
        }
        .loading-progress-bar {
          width: 220px;
        }
        @media (min-width: 480px) {
          .loading-brand-text {
            font-size: 52px;
          }
          .loading-sub-text {
            font-size: 10px;
          }
          .loading-progress-bar {
            width: 280px;
          }
        }
        @keyframes loadLogoIn {
          from { opacity: 0; transform: scale(0.88) translateY(12px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes loadSubIn {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes loadLineExpand {
          from { width: 0; opacity: 0; }
          to   { width: 220px; opacity: 1; }
        }
        @keyframes loadGlow {
          from { transform: scale(0.9); opacity: 0.6; }
          to   { transform: scale(1.15); opacity: 1; }
        }
        @keyframes loadIconPulse {
          from { opacity: 0.8; filter: drop-shadow(0 0 8px rgba(220,38,38,0.3)); }
          to   { opacity: 1; filter: drop-shadow(0 0 24px rgba(220,38,38,0.6)); }
        }
        @media (min-width: 480px) {
          @keyframes loadLineExpand {
            from { width: 0; opacity: 0; }
            to   { width: 280px; opacity: 1; }
          }
        }
      `}</style>
    </div>
  );
}
